import { distance } from "fastest-levenshtein";

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ");
}

/** Simple stem: drop trailing 's' / 'es' / 'ies'. */
function stem(s: string): string {
  if (s.length <= 3) return s;
  if (s.endsWith("ies")) return s.slice(0, -3) + "y";
  if (s.endsWith("es")) return s.slice(0, -2);
  if (s.endsWith("s")) return s.slice(0, -1);
  return s;
}

export function fuzzyMatch(a: string, b: string): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (stem(na) === stem(nb)) return true;
  // Levenshtein threshold scaled by length
  const maxLen = Math.max(na.length, nb.length);
  const threshold = maxLen <= 4 ? 1 : maxLen <= 8 ? 2 : 3;
  return distance(na, nb) <= threshold;
}

export interface Cluster<T> {
  key: string; // canonical label
  items: T[];
}

export function cluster<T>(
  items: T[],
  getValue: (t: T) => string,
): Cluster<T>[] {
  const clusters: Cluster<T>[] = [];
  for (const item of items) {
    const val = getValue(item);
    const existing = clusters.find((c) => fuzzyMatch(c.key, val));
    if (existing) {
      existing.items.push(item);
    } else {
      clusters.push({ key: normalize(val) || val, items: [item] });
    }
  }
  // sort biggest first
  return clusters.sort((a, b) => b.items.length - a.items.length);
}

/** Alignment 0-100 for a set of values: weighted by largest-cluster share. */
export function alignmentScore<T>(items: T[], getValue: (t: T) => string): number {
  if (items.length <= 1) return 100;
  const cs = cluster(items, getValue);
  // Weighted: sum of (size choose 2) over total pairs
  let aligned = 0;
  for (const c of cs) aligned += (c.items.length * (c.items.length - 1)) / 2;
  const total = (items.length * (items.length - 1)) / 2;
  return Math.round((aligned / total) * 100);
}
